/**
 * Authenticated Fetch Utility
 *
 * Wraps fetch() with:
 * - Session expiry checks (prevents requests when session is already expired)
 * - Auth headers (token + dev persona)
 * - 401 interception with token refresh coordination (same mechanism as apiClient)
 * - Retry on successful token refresh
 *
 * All fetch-based services should use this instead of raw fetch().
 */

import { authService } from '../auth/authService'
import { sessionManager, SessionExpiredError } from './sessionManager'
import { isAuthReady, waitForAuthReady } from './authReadiness'
import { announcePersonaRefusal, personaRefusalFrom } from './personaRefusal'

/**
 * Build auth headers (token + dev persona).
 * Does NOT include Content-Type — callers set that themselves.
 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}

  const token = authService.getToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Dev persona headers (matches apiClient's logic)
  const enableTestUsers = import.meta.env.DEV || import.meta.env.VITE_ENABLE_TEST_USERS === 'true'
  if (enableTestUsers) {
    const devPersonaStr = localStorage.getItem('dev_selected_persona')
    if (devPersonaStr) {
      try {
        const devPersona = JSON.parse(devPersonaStr)
        headers['X-Test-User-Id'] = devPersona.personnelNumber || 'TEST001'
        headers['X-Test-User-Roles'] = (devPersona.roles || ['employee']).join(',')
      if (devPersona.permissions?.length) {
        headers['X-Test-User-Permissions'] = devPersona.permissions.join(',')
      }
      } catch {
        console.warn('Failed to parse dev persona for headers')
      }
    }
  }

  return headers
}

/**
 * Wait for the tokenRefreshComplete event dispatched by signalTokenRefreshComplete.
 * Resolves with the new token or null on timeout.
 */
function waitForTokenRefreshComplete(timeoutMs: number): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const timer = setTimeout(() => {
      window.removeEventListener('tokenRefreshComplete', handler)
      resolve(null)
    }, timeoutMs)

    const handler = (event: Event) => {
      clearTimeout(timer)
      window.removeEventListener('tokenRefreshComplete', handler)
      const token = (event as CustomEvent<{ token: string | null }>).detail?.token ?? null
      resolve(token)
    }

    // Register BEFORE dispatching tokenRefreshNeeded to prevent race conditions
    window.addEventListener('tokenRefreshComplete', handler, { once: true })
  })
}

/**
 * Announce a refused test persona (see personaRefusal.ts). Reads a copy of the body, so
 * the caller still gets the response untouched.
 */
async function announceIfPersonaRefused(response: Response): Promise<void> {
  try {
    const body = typeof response.clone === 'function' ? await response.clone().json() : null
    const refusal = personaRefusalFrom(response.status, body?.detail)
    if (refusal) announcePersonaRefusal(refusal)
  } catch {
    // Not JSON, so not a persona refusal.
  }
}

/**
 * Authenticated fetch wrapper with 401 handling.
 *
 * Returns the raw Response — callers handle JSON parsing, 204, blobs, etc.
 * Does NOT set Content-Type (safe for FormData uploads).
 *
 * On 401: coordinates with AuthProvider via tokenRefreshNeeded/tokenRefreshComplete
 * events (same mechanism as apiClient's Axios interceptor).
 */
export async function authenticatedFetch(
  url: string,
  options?: RequestInit
): Promise<Response> {
  // Boot gate: hold authenticated requests until a usable token exists (or
  // auth is known unrecoverable) — see authReadiness.ts.
  if (!isAuthReady()) {
    await waitForAuthReady()
  }

  // Pre-flight: reject immediately if session is already expired
  if (sessionManager.isExpired()) {
    throw new SessionExpiredError()
  }

  // Build headers — merge auth headers with caller's headers
  const authHeaders = getAuthHeaders()
  const callerHeaders = options?.headers instanceof Headers
    ? Object.fromEntries((options.headers as Headers).entries())
    : (options?.headers as Record<string, string>) || {}

  const mergedHeaders = {
    ...authHeaders,
    ...callerHeaders,
  }

  // Make the request
  const response = await fetch(url, {
    ...options,
    headers: mergedHeaders,
  })

  // Not a 401 — return as-is, once a refused test persona is announced
  if (response.status !== 401) {
    if (response.status === 403 && mergedHeaders['X-Test-User-Id']) {
      await announceIfPersonaRefused(response)
    }
    return response
  }

  // Got 401 — check if session was marked expired while in-flight
  if (sessionManager.isExpired()) {
    throw new SessionExpiredError()
  }

  // Set up listener BEFORE dispatching event (prevents race condition).
  // 15s: must outlast AuthProvider's bounded MSAL settle-wait (8s) plus the
  // silent acquisition itself.
  const refreshPromise = waitForTokenRefreshComplete(15000)

  // Dispatch tokenRefreshNeeded (same event apiClient dispatches)
  // AuthProvider's isRefreshingRef guard prevents double-refresh
  if (import.meta.env.DEV) {
    console.log('🔄 authenticatedFetch: 401 received, requesting token refresh')
  }
  window.dispatchEvent(new CustomEvent('tokenRefreshNeeded'))

  // Wait for AuthProvider to complete the refresh
  const newToken = await refreshPromise

  // If session marked expired during refresh, bail
  if (sessionManager.isExpired()) {
    throw new SessionExpiredError()
  }

  // If no new token at all, mark session expired
  const oldToken = authHeaders['Authorization']?.replace('Bearer ', '')
  if (!newToken) {
    sessionManager.markExpired()
    throw new SessionExpiredError()
  }

  // Token unchanged: MSAL id tokens may not change on silent refresh.
  // Still retry — the original 401 may have been transient.
  if (newToken === oldToken) {
    if (import.meta.env.DEV) {
      console.log('⚠️ authenticatedFetch: Token unchanged after refresh — retrying with same token')
    }
  }

  // Retry with new token
  const retryHeaders = {
    ...mergedHeaders,
    'Authorization': `Bearer ${newToken}`,
  }

  const retryResponse = await fetch(url, {
    ...options,
    headers: retryHeaders,
  })

  // If retry also gets 401, session is truly expired
  if (retryResponse.status === 401) {
    sessionManager.markExpired()
    throw new SessionExpiredError()
  }

  return retryResponse
}
